import { useState, useEffect, Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { HeartHandshake, Loader2 } from 'lucide-react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Toaster } from 'sonner'
import { AuthProvider } from '@/contexts/AuthContext'
import { EntityProvider } from '@/contexts/EntityContext'
import { AppLayout, SafeAreaTopVeil } from '@/components/layout'
import { PortalLayout } from '@/components/portal/PortalLayout'
import { ErrorBoundary } from '@/components/ErrorBoundary'
// `lazyRoute` et non `lazy` : un onglet ouvert pendant un déploiement voit disparaître l'URL
// du chunk qu'il s'apprête à importer. React mémorisant le rejet, ni « Réessayer » ni le HMR
// ne rattrapent — seul un rechargement, que `lazyRoute` déclenche une fois. Voir `@/lib/lazyRoute`.
import { lazyRoute } from '@/lib/lazyRoute'

// Lazy-loaded pages — each becomes its own chunk
const HomePage = lazyRoute(() => import('@/pages/HomePage'))
const DashboardPage = lazyRoute(() => import('@/pages/DashboardPage'))
const Login = lazyRoute(() => import('@/pages/Login'))
const Signup = lazyRoute(() => import('@/pages/Signup'))
const VerifyEmail = lazyRoute(() => import('@/pages/VerifyEmail'))
const ForgotPassword = lazyRoute(() => import('@/pages/ForgotPassword'))
const ResetPassword = lazyRoute(() => import('@/pages/ResetPassword'))
const SetPassword = lazyRoute(() => import('@/pages/SetPassword'))
const ConfirmEmailChange = lazyRoute(() => import('@/pages/account/ConfirmEmailChange'))
// EPIC 49 PORT-006 — les pages d'ACCÈS à un espace externe. Elles doublent
// `/set-password`, `/forgot-password` et `/reset-password` **volontairement** :
// mêmes routes SERVEUR, coque différente. Une personne externe ne doit jamais
// voir un écran aux couleurs d'Argalis, et la page staff renvoie vers `/login`,
// qui refuse son compte par construction (PORT-002).
const PortalSetPassword = lazyRoute(() => import('@/pages/portal/PortalSetPassword'))
const PortalForgotPassword = lazyRoute(() => import('@/pages/portal/PortalForgotPassword'))
const PortalResetPassword = lazyRoute(() => import('@/pages/portal/PortalResetPassword'))
const PortalMfaVerify = lazyRoute(() => import('@/pages/portal/PortalMfaVerify'))
// EPIC 49 PORT-008 — la page de connexion et la COQUE d'un espace. `PortalLayout`
// est un FRÈRE d'`AppLayout`, jamais un enfant : il ne monte ni `EntityProvider`,
// ni `useTenantConfig`, ni `/auth/me` — les trois rendent 403 à un compte d'espace.
const PortalLoginPage = lazyRoute(() => import('@/pages/portal/PortalLogin'))
const PortalHome = lazyRoute(() => import('@/pages/portal/PortalHome'))
const PortalAccount = lazyRoute(() => import('@/pages/portal/PortalAccount'))
const PortalChooseContext = lazyRoute(() => import('@/pages/portal/PortalChooseContext'))
const PortalSessionsStub = lazyRoute(() => import('@/pages/portal/PortalSessionsStub'))
const MfaSetup = lazyRoute(() => import('@/pages/MfaSetup'))
const MfaVerify = lazyRoute(() => import('@/pages/MfaVerify'))
const SignCeremonyPage = lazyRoute(() => import('@/pages/SignCeremonyPage'))
const QuotePublicPage = lazyRoute(() => import('@/pages/QuotePublicPage'))
const QuoteRosterPage = lazyRoute(() => import('@/pages/QuoteRosterPage'))
// INSTR-016 — page publique des liens d'action (collecte / auto-déclaration).
const ActionLinkPage = lazyRoute(() => import('@/pages/ActionLinkPage'))
const NotificationsPage = lazyRoute(() => import('@/pages/notifications/NotificationsPage'))
const NotificationSettingsPage = lazyRoute(() => import('@/pages/settings/NotificationSettingsPage'))
const KnowledgeHome = lazyRoute(() => import('@/pages/knowledge/KnowledgeHome'))
const KnowledgeArticle = lazyRoute(() => import('@/pages/knowledge/KnowledgeArticle'))
const KnowledgeSearch = lazyRoute(() => import('@/pages/knowledge/KnowledgeSearch'))
const KnowledgeCategoryView = lazyRoute(() => import('@/pages/knowledge/KnowledgeCategoryView').then(m => ({ default: m.KnowledgeCategoryView })))
const DeveloperPortal = lazyRoute(() => import('@/pages/developers').then(m => ({ default: m.DeveloperPortal })))
const PlaceholderPage = lazyRoute(() => import('@/pages/PlaceholderPage'))
const CatalogPlaceholderPage = lazyRoute(() => import('@/pages/catalog/CatalogPlaceholderPage'))
const InstructorsListPage = lazyRoute(() => import('@/pages/instructors/InstructorsListPage'))
const InstructorDetailPage = lazyRoute(() => import('@/pages/instructors/InstructorDetailPage'))
// INSTR-017/018 — cockpits BUSINESS (masqués hors plan via la nav).
const InstructorRequestsPage = lazyRoute(() => import('@/pages/instructors/InstructorRequestsPage'))
const InstructorRequirementsCockpit = lazyRoute(() => import('@/pages/instructors/InstructorRequirementsCockpit'))
const InstructorCoveragePage = lazyRoute(() => import('@/pages/instructors/InstructorCoveragePage'))
const ReferentialsPage = lazyRoute(() => import('@/pages/pedagogy/ReferentialsPage'))
const AssessmentsPage = lazyRoute(() => import('@/pages/pedagogy/AssessmentsPage'))
const FormationsPage = lazyRoute(() => import('@/pages/catalog/FormationsPage'))
const FormationDetailPage = lazyRoute(() => import('@/pages/catalog/FormationDetailPage'))
const ExpensesPage = lazyRoute(() => import('@/pages/catalog/expenses/ExpensesPage'))
const ExpenseDetailPage = lazyRoute(() => import('@/pages/catalog/expenses/ExpenseDetailPage'))
const ArticlesPage = lazyRoute(() => import('@/pages/catalog/articles/ArticlesPage'))
const ArticleDetailPage = lazyRoute(() => import('@/pages/catalog/articles/ArticleDetailPage'))
const ElearningPage = lazyRoute(() => import('@/pages/pedagogy/ElearningPage'))
const ElearningEditor = lazyRoute(() => import('@/pages/pedagogy/elearning/ElearningEditor'))
const ElearningPreview = lazyRoute(() => import('@/pages/pedagogy/elearning/ElearningPreview'))
const LocationsPage = lazyRoute(() => import('@/pages/logistics/locations/LocationsPage'))
const LocationDetailPage = lazyRoute(() => import('@/pages/logistics/locations/LocationDetailPage'))
const CateringPage = lazyRoute(() => import('@/pages/logistics/catering/CateringPage'))
const CateringDetailPage = lazyRoute(() => import('@/pages/logistics/catering/CateringDetailPage'))
const AccommodationPage = lazyRoute(() => import('@/pages/logistics/accommodation/AccommodationPage'))
const AccommodationDetailPage = lazyRoute(() => import('@/pages/logistics/accommodation/AccommodationDetailPage'))
// 30_LOG / RSC — chargées à la demande comme leurs voisines : la liste porte
// une DataTable serveur complète, la fiche tous ses onglets.
const ResourcesPage = lazyRoute(() => import('@/pages/logistics/resources/ResourcesPage'))
const ResourceDetailPage = lazyRoute(() => import('@/pages/logistics/resources/ResourceDetailPage'))
const ControlTypesPage = lazyRoute(() => import('@/pages/settings/logistics/ControlTypesPage'))
const ResourceReferentielPage = lazyRoute(() => import('@/pages/settings/logistics/ResourceReferentielPage'))
const ClientsListPage = lazyRoute(() => import('@/pages/crm/ClientsListPage'))
const ClientDetailPage = lazyRoute(() => import('@/pages/crm/ClientDetailPage'))
// TND-013 — la vue transverse « Révisions de prix » (sous-entrée de Clients).
const PriceRevisionsPage = lazyRoute(() => import('@/pages/crm/price-revisions/PriceRevisionsPage'))
const LearnersListPage = lazyRoute(() => import('@/pages/crm/LearnersListPage'))
const LearnerDetailPage = lazyRoute(() => import('@/pages/crm/LearnerDetailPage'))
const DirectoryPage = lazyRoute(() => import('@/pages/directory/DirectoryPage'))
// EPIC 11 SALE — Devis (commercial)
const QuotesListPage = lazyRoute(() => import('@/pages/sales/QuotesListPage'))
// EPIC 40 TND — Appels d'offres. `lazyRoute` et non `lazy` : un onglet
// ouvert pendant un déploiement voit son chunk disparaître, et c'est lui
// qui déclenche le rechargement une fois.
const TendersListPage = lazyRoute(() => import('@/pages/sales/tenders/TendersListPage'))
const TenderDetailPage = lazyRoute(() => import('@/pages/sales/tenders/TenderDetailPage'))
// TND-012 — la table GLOBALE des contrats cadres, tous clients confondus. Elle
/*
 * EPIC 40 — les trois écrans autonomes du module Appels d'offres, livrés le
 * 2026-08-31. Ils sont chargés en paresseux : aucun n'est un point
 * d'atterrissage, et leurs bundles n'ont pas à peser sur le premier écran.
 */
const TenderLibraryPage = lazyRoute(
    () => import('@/pages/sales/tenders/library/TenderLibraryPage'),
)
const TenderPipelineSettingsPage = lazyRoute(
    () => import('@/pages/sales/tenders/settings/TenderPipelineSettingsPage'),
)
const QuoteDetailPage = lazyRoute(() => import('@/pages/sales/QuoteDetailPage'))
// SALE-026 — bulletin de commission + éditeur de règles. `lazyRoute` et non
// `React.lazy` nu : un onglet resté ouvert pendant un déploiement rejouerait
// l'import d'un chunk disparu et afficherait « Failed to fetch dynamically
// imported module ».
const CommissionStatementPage = lazyRoute(() => import('@/pages/sales/CommissionStatementPage'))
const SalesPilotagePage = lazyRoute(() => import('@/pages/sales/SalesPilotagePage'))
const NonConformitiesPage = lazyRoute(() => import('@/pages/quality/NonConformitiesPage'))
const NonConformityDetailPage = lazyRoute(() => import('@/pages/quality/NonConformityDetailPage'))
const SatisfactionPage = lazyRoute(() => import('@/pages/quality/satisfaction/SatisfactionPage'))
const SatisfactionQuestionnaireEditorPage = lazyRoute(
    () => import('@/pages/quality/satisfaction/QuestionnaireEditorPage'),
)
const ActionPlanPage = lazyRoute(() => import('@/pages/quality/ActionPlanPage'))
const ActionDetailPage = lazyRoute(() => import('@/pages/quality/ActionDetailPage'))
// Menu latéral = console d'exécution (runs passé/futur) ; Réglages = atelier des
// définitions (gestion + création/édition). L'éditeur est monté sous /settings/workflows.
const WorkflowExecutionsPage = lazyRoute(() => import('@/pages/automation/WorkflowExecutionsPage'))
const WorkflowManagementPage = lazyRoute(() => import('@/pages/automation/WorkflowManagementPage'))
const WorkflowEditorPage = lazyRoute(() => import('@/pages/automation/WorkflowEditorPage'))
const ChangelogPage = lazyRoute(() => import('@/pages/ChangelogPage'))
const NotFoundPage = lazyRoute(() => import('@/pages/NotFoundPage'))
const SettingsHub = lazyRoute(() => import('@/pages/settings/SettingsHub'))
const GeneralSettings = lazyRoute(() => import('@/pages/settings/GeneralSettings'))
// EPIC 36 CUSTOM-001 — réglages des champs personnalisés et obligatoires.
const FieldConfigPage = lazyRoute(() => import('@/pages/settings/fieldConfig/FieldConfigPage'))
const EntitiesPage = lazyRoute(() => import('@/pages/settings/EntitiesPage'))
const UsersRolesPage = lazyRoute(() => import('@/pages/settings/UsersRolesPage'))
const AuditTrailPage = lazyRoute(() => import('@/pages/settings/AuditTrailPage'))
const ProfessionsManagementPage = lazyRoute(() => import('@/pages/settings/ProfessionsManagementPage'))
const NirRefusalReasonsManagementPage = lazyRoute(() => import('@/pages/settings/NirRefusalReasonsManagementPage'))
const SentEmailsPage = lazyRoute(() => import('@/pages/settings/SentEmailsPage'))
const DocumentTemplatesPage = lazyRoute(() => import('@/pages/settings/DocumentTemplatesPage'))
const ChecklistTemplatesPage = lazyRoute(() => import('@/pages/checklists/ChecklistTemplatesPage'))

const ChecklistEditorPage = lazyRoute(() => import('@/pages/checklists/ChecklistEditorPage'))
const TagsSettingsPage = lazyRoute(() => import('@/pages/settings/TagsSettingsPage'))
const EmailTemplatesPage = lazyRoute(() => import('@/pages/settings/EmailTemplatesPage'))
const GedPage = lazyRoute(() => import('@/pages/settings/GedPage'))
const ExportsPage = lazyRoute(() => import('@/pages/settings/ExportsPage'))
const ApiKeysPage = lazyRoute(() => import('@/pages/settings/ApiKeysPage'))
const AppearancePage = lazyRoute(() => import('@/pages/account/AppearancePage'))
const AccountNotificationsPage = lazyRoute(() => import('@/pages/account/NotificationsPage'))
const ProfilePage = lazyRoute(() => import('@/pages/account/ProfilePage'))
const SecurityPage = lazyRoute(() => import('@/pages/account/SecurityPage'))

/**
 * `/settings/checklists/*` → `/checklists/*`, sous-chemin ET query préservés.
 *
 * `<Navigate to="/checklists">` ne suffisait pas : il aplatit toute la branche
 * sur la banque, donc un lien vers UN modèle perdait son identifiant. Ici on ne
 * remplace que le préfixe, ce qui fait aboutir aussi bien `/settings/checklists`
 * que `/settings/checklists/chktpl_…`.
 */
function LegacyChecklistsRedirect() {
    const { pathname, search, hash } = useLocation()
    const cible = pathname.replace(/^\/settings\/checklists/, '/checklists') || '/checklists'
    return <Navigate to={`${cible}${search}${hash}`} replace />
}

// Dev-only: tree-shaked in production via import.meta.env.DEV
const DataTablePlayground = import.meta.env.DEV
    ? lazyRoute(() => import('@/pages/dev/DataTablePlayground'))
    : () => null
const DocumentEditorPlayground = import.meta.env.DEV
    ? lazyRoute(() => import('@/pages/dev/DocumentEditorPlayground'))
    : () => null

/**
 * Route → chunk prefetch map.
 * Called on hover in the sidebar to preload the chunk before the user clicks.
 * Each entry triggers the same dynamic import() as the lazy() above — Vite deduplicates.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const routePrefetch: Record<string, () => void> = {
    '/clients/price-revisions': () => { import('@/pages/crm/price-revisions/PriceRevisionsPage') },
    '/catalog/formations': () => { import('@/pages/catalog/FormationsPage') },
    '/catalog/prestations': () => { import('@/pages/catalog/CatalogPlaceholderPage') },
    '/catalog/articles': () => { import('@/pages/catalog/articles/ArticlesPage') },
    '/catalog/frais': () => { import('@/pages/catalog/expenses/ExpensesPage') },
    '/pedagogy/assessments': () => { import('@/pages/pedagogy/AssessmentsPage') },
    '/pedagogy/elearning': () => { import('@/pages/pedagogy/ElearningPage') },
    '/pedagogy/referentials': () => { import('@/pages/pedagogy/ReferentialsPage') },
    '/logistics/locations': () => { import('@/pages/logistics/locations/LocationsPage') },
    '/logistics/catering': () => { import('@/pages/logistics/catering/CateringPage') },
    '/logistics/accommodation': () => { import('@/pages/logistics/accommodation/AccommodationPage') },
    '/logistics/resources': () => import('@/pages/logistics/resources/ResourcesPage'),
    '/settings/logistics/control-types': () => import('@/pages/settings/logistics/ControlTypesPage'),
    '/settings/logistics/resource-referentiel': () => import('@/pages/settings/logistics/ResourceReferentielPage'),
    // Section Intervenants — les trois écrans de la section, dont deux servent
    // des tableaux serveur : leur chunk pèse, et l'attendre au clic se voit.
    '/instructors/requests': () => { import('@/pages/instructors/InstructorRequestsPage') },
    '/instructors/requirements': () => { import('@/pages/instructors/InstructorRequirementsCockpit') },
    '/instructors/coverage': () => { import('@/pages/instructors/InstructorCoveragePage') },
    '/sales/quotes': () => { import('@/pages/sales/QuotesListPage') },
    // Le listing des AO sert un tableau serveur : son chunk pèse, et
    // l'attendre au clic se voit. Préchargé au survol de l'entrée de menu.
    '/sales/tenders': () => { import('@/pages/sales/tenders/TendersListPage') },
    '/sales/tenders/library': () => { import('@/pages/sales/tenders/library/TenderLibraryPage') },
    '/quality/non-conformities': () => { import('@/pages/quality/NonConformitiesPage') },
    '/quality/action-plan': () => { import('@/pages/quality/ActionPlanPage') },
    '/quality/qualiopi': () => { import('@/pages/PlaceholderPage') },
    '/quality/satisfaction': () => { import('@/pages/quality/satisfaction/SatisfactionPage') },
    '/settings': () => { import('@/pages/settings/SettingsHub') },
}

function LoadingSpinner() {
    return (
        <div className="flex items-center justify-center min-h-[40vh]">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
    )
}

/**
 * Theme-aware Toaster: watches the `dark` class on <html> to sync sonner theme.
 */
function ThemedToaster() {
    const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'))

    useEffect(() => {
        const observer = new MutationObserver(() => {
            setIsDark(document.documentElement.classList.contains('dark'))
        })
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
        return () => observer.disconnect()
    }, [])

    return (
        <Toaster
            position="top-right"
            richColors
            closeButton
            theme={isDark ? 'dark' : 'light'}
            offset={{ top: 'calc(24px + env(safe-area-inset-top))' }}
            mobileOffset={{ top: 'calc(16px + env(safe-area-inset-top))' }}
        />
    )
}

function App() {
    const { t } = useTranslation()

    return (
        <AuthProvider>
            {/* TECH-011 — Voile opaque sur la safe-area-top (Dynamic Island /
                encoche iPhone). Placé au plus haut niveau pour couvrir toutes
                les routes (publiques + AppLayout + 404) sans duplication. */}
            <SafeAreaTopVeil />
            <ThemedToaster />

            <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                    {/* Public routes */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/verify-email" element={<VerifyEmail />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/set-password" element={<SetPassword />} />
                    <Route path="/account/confirm-email-change" element={<ConfirmEmailChange />} />
                    <Route path="/mfa-setup" element={<MfaSetup />} />
                    <Route path="/mfa-verify" element={<MfaVerify />} />

                    {/* EPIC 49 PORT-006 — l'accès à un espace externe, sous `/espace`. */}
                    <Route path="/espace/set-password" element={<PortalSetPassword />} />
                    <Route path="/espace/forgot-password" element={<PortalForgotPassword />} />
                    <Route path="/espace/reset-password" element={<PortalResetPassword />} />
                    <Route path="/espace/mfa-verify" element={<PortalMfaVerify />} />
                    <Route path="/espace" element={<PortalLoginPage />} />

                    {/* EPIC 49 PORT-008 — la coque d'un espace. ⚠️ L'affectation
                        est NOMINATIVE : les quatre routes d'authentification
                        ci-dessus portent leur propre coque, et TOUT le reste de
                        `/espace` passe par `PortalLayout`. Un joker sur
                        `/espace/*` placé plus haut ferait tomber un écran de
                        facette sous la coque de connexion. */}
                    <Route element={<PortalLayout />}>
                        <Route path="/espace/app" element={<PortalHome />} />
                        <Route path="/espace/app/compte" element={<PortalAccount />} />
                        <Route path="/espace/app/sessions" element={<PortalSessionsStub />} />
                    </Route>
                    <Route path="/espace/choisir" element={<PortalChooseContext />} />

                    {/* Page publique de signature — SIGN-005 (no-auth, jeton opaque) */}
                    <Route path="/sign/:token" element={<SignCeremonyPage />} />
                    <Route path="/action/:token" element={<ActionLinkPage />} />
                    {/* Consultation publique d'un devis — SALE-012 (no-auth, jeton
                        haché). Lien DURABLE et consultation SEULE : aucune acceptation
                        ici, l'engagement passe par le canal signature. */}
                    <Route path="/quote/:token" element={<QuotePublicPage />} />
                    {/* Inscription des participants par le CLIENT — SALE-007
                        §7.2quater (no-auth, jeton haché). Différence de nature
                        avec la route ci-dessus : celle-ci permet d'ÉCRIRE, d'où
                        un index de jetons séparé côté serveur et un plafond de
                        places recalculé à chaque envoi. */}
                    <Route path="/inscription/:token" element={<QuoteRosterPage />} />

                    {/* Public knowledge routes (no auth required) */}
                    <Route path="/knowledge" element={<KnowledgeHome />} />
                    <Route path="/knowledge/category" element={<KnowledgeCategoryView />} />
                    <Route path="/knowledge/search" element={<KnowledgeSearch />} />
                    <Route path="/knowledge/:slug" element={<KnowledgeArticle />} />
                    <Route path="/developers" element={<DeveloperPortal />} />

                    {/* Fullscreen protected routes (no sidebar/header, wrapped in EntityProvider) */}
                    <Route path="/pedagogy/elearning/:moduleId/preview" element={<EntityProvider><ElearningPreview /></EntityProvider>} />

                    {/* Protected routes with AppLayout */}
                    <Route element={<AppLayout />}>
                        <Route path="/" element={<HomePage />} />
                        <Route path="/dashboard" element={<DashboardPage />} />

                        {/* Placeholder Routes for Navigation Testing */}
                        <Route path="/sessions" element={<PlaceholderPage />} />
                        <Route path="/billing" element={<PlaceholderPage />} />
                        {/* EPIC 11 SALE — Devis. L'ancien placeholder /quotes redirige vers /sales/quotes. */}
                        <Route path="/quotes" element={<Navigate to="/sales/quotes" replace />} />
                        {/* 🔴 Sous ErrorBoundary, comme ses trois sœurs. C'était la
                            SEULE route du module sans filet — et c'est elle qui
                            monte le tableau de bord (20+ tuiles recharts) et le
                            kanban. Une TypeError dans une tuile emportait donc tout
                            le shell : plus de menu, plus de navigation, la page de
                            repli globale. Sur la fiche devis, l'utilisateur garde
                            les siens. */}
                        <Route
                            path="/sales/quotes"
                            element={
                                <ErrorBoundary>
                                    <QuotesListPage />
                                </ErrorBoundary>
                            }
                        />
                        <Route path="/sales/quotes/:id" element={<ErrorBoundary><QuoteDetailPage /></ErrorBoundary>} />
                        {/* EPIC 40 TND — Appels d'offres & marchés publics.
                            Le gating (permission `tenders:read` + plan
                            `TENDERS`) est porté par la NAV ; la page elle-même
                            reçoit un 403 du serveur hors plan — la route reste
                            atteignable par URL directe, et c'est voulu : un
                            lien de notification déjà parti doit aboutir sur une
                            explication, pas sur un 404 muet. */}
                        <Route
                            path="/sales/tenders"
                            element={
                                <ErrorBoundary>
                                    <TendersListPage />
                                </ErrorBoundary>
                            }
                        />

                        {/* 🔴 Les segments STATIQUES passent AVANT `/:id` :
                            React Router résout dans l'ordre, et « watch » serait
                            sinon lu comme un identifiant d'appel d'offres — la
                            fiche partirait chercher un AO nommé « watch » et
                            rendrait son état « introuvable ». */}
                        {/* TND-017 — l'ancienne page de veille. Un avis capté
                            EST désormais une fiche au stage « Veille » : il se
                            lit, se trie et se refuse dans le tableau des appels
                            d'offres, avec le même vocabulaire de motifs que le
                            reste du module (migration 660).

                            ⚠️ La redirection reste : la notification de digest
                            envoyée avant ce changement porte encore un lien vers
                            cette adresse, et un courriel déjà parti ne se
                            réécrit pas. Sans elle, il rendrait « introuvable ».
                            La règle d'ordre ci-dessus vaut toujours — « watch »
                            doit être résolu AVANT `/sales/tenders/:id`. */}
                        <Route
                            path="/sales/tenders/watch"
                            element={<Navigate to="/sales/tenders" replace />}
                        />
                        {/* TND-019 — la bibliothèque de réponse (références de
                            marché et blocs de mémoire). */}
                        <Route
                            path="/sales/tenders/library"
                            element={<ErrorBoundary><TenderLibraryPage /></ErrorBoundary>}
                        />
                        {/*
                          * 🔴 **Les réglages du pipeline vivent DANS le module,
                          * pas dans Paramètres** — règle PO du 2026-09-01 : tout
                          * ce qui concerne les appels d'offres se gère depuis
                          * leurs propres écrans. Ils étaient sous
                          * `/settings/tenders-pipeline`, exposés par le hub, et
                          * personne ne les y trouvait.
                          *
                          * ⚠️ Déclarée AVANT `/sales/tenders/:id`, sinon
                          * « settings » serait lu comme un identifiant d'appel
                          * d'offres et la fiche rendrait un 404.
                          */}
                        <Route
                            path="/sales/tenders/settings"
                            element={
                                <ErrorBoundary>
                                    <TenderPipelineSettingsPage />
                                </ErrorBoundary>
                            }
                        />
                        <Route
                            path="/sales/tenders/:id"
                            element={<ErrorBoundary><TenderDetailPage /></ErrorBoundary>}
                        />
                        {/* SALE-025 — file d'attente des dérogations de remise.
                            Le gating (permission + plan) est porté par la nav ;
                            la page elle-même reçoit un 403 du serveur hors plan. */}
                        {/* SALE-025 + SALE-026 (2026-08-13) — les deux suivis du
                            responsable fusionnent en un écran à deux onglets.
                            L'ancienne route REDIRIGE : un lien de notification
                            déjà parti doit continuer d'aboutir. */}
                        <Route
                            path="/sales/pilotage"
                            element={<ErrorBoundary><SalesPilotagePage /></ErrorBoundary>}
                        />
                        <Route
                            path="/sales/discount-approvals"
                            element={<Navigate to="/sales/pilotage?tab=approvals" replace />}
                        />
                        {/* SALE-026 — le bulletin est un tableau FINANCIER : sous
                            ErrorBoundary, pour qu'un plantage n'emporte pas le shell. */}
                        <Route
                            path="/sales/commissions"
                            element={<ErrorBoundary><CommissionStatementPage /></ErrorBoundary>}
                        />
                        {/* EPIC 11 SALE — sous-entrées Commercial en attente de livraison (SALE-014/015/016/017) :
                            placeholder « bientôt disponible » (PlaceholderPage lit le libellé depuis la nav)
                            pour éviter un 404 tant que la fonctionnalité réelle n'est pas livrée. */}
                        <Route path="/sales/pending-requests" element={<PlaceholderPage />} />
                        <Route path="/sales/waitlists" element={<PlaceholderPage />} />
                        <Route path="/sales/planning-requests" element={<PlaceholderPage />} />
                        <Route path="/sales/renewals" element={<PlaceholderPage />} />
                        {/* EPIC 12 WKF — Menu latéral « Workflows » = console d'EXÉCUTION
                            (runs passé/futur, tous plans). La GESTION des définitions
                            (création + activer/pause + éditeur) vit dans Réglages →
                            Automatisation (/settings/workflows*, cf. plus bas). Gate de
                            plan WORKFLOWS porté par la nav + la page. */}
                        {/* ErrorBoundary comme les 3 routes de Réglages : c'est la page du
                            menu de gauche, celle qu'on ouvre le plus — un plantage de rendu
                            y donnait un écran BLANC au lieu d'un repli. */}
                        <Route path="/automation/workflows" element={<ErrorBoundary><WorkflowExecutionsPage /></ErrorBoundary>} />
                        {/* Anciennes routes éditeur → Réglages (bookmarks locaux). */}
                        <Route path="/automation/workflows/new" element={<Navigate to="/settings/workflows/new" replace />} />
                        {/* /clients → ClientsListPage (CLI-001), cf. routes plus bas */}
                        {/* /learners → LearnersListPage (LRN-001), cf. routes plus bas */}
                        <Route path="/catalog/formations" element={<FormationsPage />} />
                        <Route path="/catalog/formations/:productId" element={<ErrorBoundary><FormationDetailPage /></ErrorBoundary>} />
                        <Route path="/catalog/prestations" element={<CatalogPlaceholderPage title={t('nav.catalogPrestations')} description={t('nav.catalogPrestationsDesc')} icon={HeartHandshake} />} />
                        <Route path="/catalog/articles" element={<ArticlesPage />} />
                        <Route path="/catalog/articles/:articleId" element={<ErrorBoundary><ArticleDetailPage /></ErrorBoundary>} />
                        <Route path="/catalog/frais" element={<ExpensesPage />} />
                        <Route path="/catalog/frais/:expenseId" element={<ErrorBoundary><ExpenseDetailPage /></ErrorBoundary>} />
                        <Route path="/pedagogy/referentials" element={<ReferentialsPage />} />
                        <Route path="/pedagogy/assessments" element={<AssessmentsPage />} />
                        <Route path="/pedagogy/elearning" element={<ElearningPage />} />
                        <Route path="/pedagogy/elearning/:moduleId" element={<ElearningEditor />} />
                        <Route path="/pedagogy/*" element={<PlaceholderPage />} />
                        {/* Logistics Routes (30_LOG) */}
                        <Route path="/logistics/locations" element={<LocationsPage />} />
                        <Route path="/logistics/locations/:locationId" element={<LocationDetailPage />} />
                        <Route path="/logistics/catering" element={<CateringPage />} />
                        <Route path="/logistics/catering/:providerId" element={<CateringDetailPage />} />
                        <Route path="/logistics/accommodation" element={<AccommodationPage />} />
                        <Route path="/logistics/accommodation/:providerId" element={<AccommodationDetailPage />} />
            {/* 30_LOG / RSC — la LISTE est déclarée avant `/:resourceId`, sinon
                le paramètre capturerait les futures routes littérales
                (`/compliance`, `/planning`). Même règle que `/instructors`. */}
            {/* Les DEUX vues du matériel — parc et vérifications — vivent sous
                cette seule route, distinguées par `?view=`. `/compliance` a été
                RETIRÉE : ses appelants ont été renommés (menu, deux articles
                d'aide, référence d'API) plutôt que maintenus par une
                redirection que plus personne n'aurait su retirer. */}
            <Route path="/logistics/resources" element={<ErrorBoundary><ResourcesPage /></ErrorBoundary>} />
            <Route path="/settings/logistics/control-types" element={<ErrorBoundary><ControlTypesPage /></ErrorBoundary>} />
            {/* RSC-002 §4 — le référentiel de typologie : familles, sous-types,
                champs personnalisés. Onze routes qui n'avaient aucun appelant. */}
            <Route path="/settings/logistics/resource-referentiel" element={<ErrorBoundary><ResourceReferentielPage /></ErrorBoundary>} />
            <Route path="/logistics/resources/:resourceId" element={<ErrorBoundary><ResourceDetailPage /></ErrorBoundary>} />

                        {/* CRM Routes (CLI-001) — URLs top-level (voir spec §4.1) */}
                        <Route path="/clients" element={<ClientsListPage />} />
                        {/* TND-013 — déclarée AVANT la fiche : un segment statique. */}
                        <Route path="/clients/price-revisions" element={<PriceRevisionsPage />} />
                        <Route path="/clients/:id" element={<ClientDetailPage />} />
                        {/* Page transverse contacts retirée 2026-05-02 —
                            sera remplacée par DIR-001 /directory (Annuaire global). */}
                        <Route path="/learners" element={<LearnersListPage />} />
                        <Route path="/learners/:id" element={<LearnerDetailPage />} />

                        {/* DIR-001 — Annuaire transverse (lecture seule) */}
                        <Route path="/directory" element={<DirectoryPage />} />

                        {/* WKF-013 — Centre de notifications & tâches (tous plans) */}
                        <Route path="/notifications" element={<NotificationsPage />} />

                        {/* `ErrorBoundary` par route, comme les fiches sœurs
                            (/sales/quotes/:id, /catalog/formations/:productId…) :
                            sans elle, un rendu qui casse dans UN onglet remonte
                            jusqu'à la barrière racine de main.tsx et emporte la
                            navigation et l'en-tête avec lui. Le vivier monte la
                            carte Leaflet, la fiche monte 11 onglets. */}
                        <Route path="/instructors" element={<ErrorBoundary><InstructorsListPage /></ErrorBoundary>} />
                        {/* Déclarée AVANT `/instructors/:id`, comme ses sœurs :
                            sinon le paramètre capturerait « requests ». */}
                        <Route path="/instructors/requests" element={<InstructorRequestsPage />} />
                        <Route path="/instructors/requirements" element={<InstructorRequirementsCockpit />} />
                        <Route path="/instructors/coverage" element={<InstructorCoveragePage />} />
                        <Route path="/instructors/:id" element={<ErrorBoundary><InstructorDetailPage /></ErrorBoundary>} />
                        <Route path="/referral" element={<PlaceholderPage />} />

                        {/* Settings Routes */}
                        <Route path="/settings" element={<SettingsHub />} />
                        {/* NOTIF-001 (World C) — Réglages notifications de l'organisme (3 onglets) */}
                        <Route path="/settings/notifications" element={<NotificationSettingsPage />} />
                        {/* Ancienne console admin des tâches → onglet du centre de réglages (redirection) */}
                        <Route
                            path="/settings/tasks"
                            element={<Navigate to="/settings/notifications?tab=tasks-admin" replace />}
                        />
                        {/* WKF-002/003 — Atelier des workflows (Réglages → Automatisation).
                            Gestion des définitions (liste + activer/pause TOUS plans) +
                            éditeur. Création/édition gatée WORKFLOWS_AUTHORING (Business)
                            côté page ; visible dès le flag WORKFLOWS. */}
                        <Route path="/settings/workflows" element={<ErrorBoundary><WorkflowManagementPage /></ErrorBoundary>} />
                        <Route path="/settings/workflows/new" element={<ErrorBoundary><WorkflowEditorPage /></ErrorBoundary>} />
                        <Route path="/settings/workflows/:id/edit" element={<ErrorBoundary><WorkflowEditorPage /></ErrorBoundary>} />
                        <Route path="/settings/general" element={<GeneralSettings />} />
                        {/* EPIC 36 CUSTOM-001 §5 — champs personnalisés et obligatoires, fiche
                            par fiche. Chemin LITTÉRAL : la carte du hub y mène, et le témoin
                            BH-078 relit cette table de routes par expression régulière. */}
                        <Route path="/settings/field-configuration" element={<ErrorBoundary><FieldConfigPage /></ErrorBoundary>} />
                        {/* SALE-008 — pipeline commercial (statuts + raisons de perte). */}
                        <Route path="/settings/entities" element={<EntitiesPage />} />
                        <Route path="/settings/users" element={<UsersRolesPage />} />
                        <Route path="/settings/audit" element={<AuditTrailPage />} />
                        <Route path="/settings/sent-emails" element={<SentEmailsPage />} />
                        <Route path="/settings/document-templates" element={<DocumentTemplatesPage />} />
                        {/* EPIC 43_CHK — la banque des modèles vit dans le MENU
                            (« Suivi & automatisation → Checklists »), pas dans les
                            Réglages : ses modèles sont le matériau de travail du
                            module, pas sa configuration (arbitrage PO 2026-08-30).
                            L'éditeur /checklists/:templateId arrive avec CHK-005. */}
                        <Route path="/checklists" element={<ChecklistTemplatesPage />} />
                        {/* CHK-005 — l'atelier d'un modèle. Route ENFANT :
                            « /checklists » reste la banque. */}
                        <Route path="/checklists/:templateId" element={<ChecklistEditorPage />} />
                        {/* L'ancienne adresse a vécu une journée, mais elle est déjà
                            citée : route de repli de la catégorie ⌘K (CHK-017 §7.2).
                            Une redirection d'une ligne coûte moins qu'un lien mort.
                            ⚠️ Le `/*` n'est PAS décoratif : rediriger la seule page
                            d'index laissait `/settings/checklists/{id}` en 404 — le
                            défaut fabriqué À LA FRONTIÈRE du déménagement, vu par le
                            PO le 2026-08-31 juste après avoir créé son 1er modèle. */}
                        <Route path="/settings/checklists/*" element={<LegacyChecklistsRedirect />} />
                        <Route path="/settings/email-templates" element={<EmailTemplatesPage />} />
                        <Route path="/settings/ged" element={<GedPage />} />
                        <Route path="/settings/exports" element={<ExportsPage />} />
                        <Route path="/settings/api-keys" element={<ApiKeysPage />} />
                        <Route path="/settings/professions" element={<ProfessionsManagementPage />} />
                        <Route path="/settings/nir-refusal-reasons" element={<NirRefusalReasonsManagementPage />} />
                        <Route
                            path="/settings/tags"
                            element={
                                <ErrorBoundary>
                                    <TagsSettingsPage />
                                </ErrorBoundary>
                            }
                        />
                        {/* TND-003 — le référentiel du pipeline appels d'offres :
                            statuts, sous-étapes, motifs d'issue. Rangé dans les
                            RÉGLAGES et non dans le module : c'est une
                            configuration d'entité, qu'on touche rarement et qui
                            n'a rien à faire dans le chemin de travail
                            quotidien. */}
                        {/* L'ancienne adresse REDIRIGE : des signets et des
                            articles d'aide la citent déjà. */}
                        <Route
                            path="/settings/tenders-pipeline"
                            element={<Navigate to="/sales/tenders/settings" replace />}
                        />

                        {/* Account Routes (PREF) */}
                        <Route path="/account/profile" element={<ProfilePage />} />
                        <Route path="/account/appearance" element={<AppearancePage />} />
                        <Route path="/account/notifications" element={<AccountNotificationsPage />} />
                        <Route path="/account/security" element={<SecurityPage />} />

                        {/* Quality Submenu Routes */}
                        {/* QUAL-001/002 — routes explicites AVANT le wildcard */}
                        <Route path="/quality/non-conformities" element={<NonConformitiesPage />} />
                        <Route path="/quality/non-conformities/:id" element={<ErrorBoundary><NonConformityDetailPage /></ErrorBoundary>} />
                        <Route path="/quality/action-plan" element={<ActionPlanPage />} />
                        <Route path="/quality/actions/:id" element={<ErrorBoundary><ActionDetailPage /></ErrorBoundary>} />
                        <Route path="/quality/satisfaction" element={<SatisfactionPage />} />
                        <Route path="/quality/satisfaction/questionnaires/:questionnaireId" element={<ErrorBoundary><SatisfactionQuestionnaireEditorPage /></ErrorBoundary>} />
                        <Route path="/quality/*" element={<PlaceholderPage />} />

                        {/* Changelog */}
                        <Route path="/changelog" element={<ChangelogPage />} />

                        {/* Dev Playground (dev only, tree-shaked in prod) */}
                        {import.meta.env.DEV && (
                            <>
                                <Route path="/dev/playground" element={<DataTablePlayground />} />
                                <Route path="/dev/document-editor" element={<DocumentEditorPlayground />} />
                            </>
                        )}
                    </Route>

                    {/* Catch-all 404 */}
                    <Route path="*" element={<NotFoundPage />} />
                </Routes>
            </Suspense>
        </AuthProvider>
    )
}

export default App
